import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Sheet, Stack, Typography } from "@financeable-com-au/financeable-ui";
// eslint-disable-next-line import/no-unresolved
// eslint-disable-next-line import/no-unresolved
import { EntityServiceCardGroup, type EntityServiceCardProps } from "@/components/Entity/EntityServiceCard";
// eslint-disable-next-line import/no-unresolved
import { PaidServicesTooltip } from "@/components/PaidServicesTooltip";
// Months selector hidden for now, see EMPLOYMENT_INCOME_VERIFICATION_DEFAULT_MONTHS.
// // @ts-expect-error: ignoring as it is existing js file
// // eslint-disable-next-line import/no-unresolved
// import RadioButtonGroup from "@/components/Utils/RadioButtonGroup";
import {
  ADDRESS_STATUS_TYPE,
  DOCUMENT_TYPES,
  EMPLOYMENT_INCOME_VERIFICATION_COPY,
  EMPLOYMENT_INCOME_VERIFICATION_FIELD_LABELS,
  EMPLOYMENT_INCOME_VERIFICATION_MONTHS,
  EMPLOYMENT_INCOME_VERIFICATION_REPORT_PDF_STATUS,
  EMPLOYMENT_INCOME_VERIFICATION_REQUIREMENT_LABELS,
  EMPLOYMENT_INCOME_VERIFICATION_STATUS,
  ERRORS_MESSAGE,
  SERVICE_PROVIDER_LOGOS,
  privacyDocsNameList,
  type EmploymentIncomeVerificationMonths,
  type EmploymentIncomeVerificationRequirement,
  // eslint-disable-next-line import/no-unresolved
} from "@/constants";
// eslint-disable-next-line import/no-unresolved
import { usePaidServices } from "@/hooks";
// eslint-disable-next-line import/no-unresolved
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
// @ts-expect-error: ignoring as it is existing js file
// eslint-disable-next-line import/no-unresolved
import { requestEmploymentIncomeVerification, userDetailsSelector } from "@/store/slices/applicationFormSlice";
// eslint-disable-next-line import/no-unresolved
import { openSnackbar } from "@/store/slices/snackbarSlice";
import type {
  ApplicationFormSlice,
  Customer,
  EmploymentIncomeVerificationSummary,
  UserDetailsState,
  VerifiedEmployer,
  VerifiedEmployment,
} from "@/store/slices/types/applicationFormSlice";
import type { UserPermissionsDocument, UserSlice, UserState } from "@/store/slices/types/userSlice";
// @ts-expect-error: ignoring as it is existing js file
// eslint-disable-next-line import/no-unresolved
import { userPermissionsSelector, userSelector } from "@/store/slices/userSlice";
import type { AppDispatch } from "@/store/store";
// eslint-disable-next-line import/no-unresolved
import { getErrorToastPayload, getSuccessToastPayload, getToastSuccessMessage } from "@/utils/commonFunctions";
// eslint-disable-next-line import/no-unresolved
import { formatToReadableDate } from "@/utils/formatDate";
// eslint-disable-next-line import/no-unresolved
import { formatCurrencyValue } from "@/utils/formatNumber";
import type { IDispatchResponse } from "@/utils/interface";
import { EmploymentIncomeVerificationModal } from "./EmploymentIncomeVerificationModal";
// @ts-expect-error: ignoring as it is existing js file
import Document from "../Document";

export type EmploymentIncomeVerificationCustomer = Pick<Customer, "_id" | "firstName" | "lastName" | "dateOfBirth"> &
  Partial<Pick<Customer, "IDNumber" | "addresses" | "documents" | "employmentIncomeVerification">>;

export interface EmploymentIncomeVerificationProps {
  customer: EmploymentIncomeVerificationCustomer;
  applicationId: string;
  isDisableEdit?: boolean;
}

interface SummaryField {
  label: string;
  value: string;
}

const isPresent = (value: unknown): boolean => value !== undefined && value !== null && value !== "";

/** Australian postcodes are four digits; one stored as a number may have lost its leading zero, as the server allows. */
const hasPostcode = (postCode: unknown): boolean => /^\d{3,4}$/.test(String(postCode ?? "").replace(/\D/g, ""));

/** The current address, or one with no status at all, as the server picks it. */
const currentAddressOf = (addresses: EmploymentIncomeVerificationCustomer["addresses"]) =>
  addresses?.find((address) => address?.status === ADDRESS_STATUS_TYPE.CURRENT) ??
  addresses?.find((address) => address?.status !== ADDRESS_STATUS_TYPE.PREVIOUS);

/**
 * The details the server refuses to order a verification without, checked here first so the broker is told before
 * clicking rather than after. The server repeats every check: this only decides what the card offers.
 */
export const getMissingRequirements = (
  customer: EmploymentIncomeVerificationCustomer,
): EmploymentIncomeVerificationRequirement[] => {
  const missing: EmploymentIncomeVerificationRequirement[] = [];

  if (!customer.firstName?.trim()) missing.push("firstName");
  if (!customer.lastName?.trim()) missing.push("lastName");
  if (!customer.dateOfBirth?.trim()) missing.push("dateOfBirth");
  // The one ID number field holds a driver licence or a passport number, as the customer's ID type says.
  if (!customer.IDNumber?.trim()) missing.push("idNumber");
  if (!hasPostcode(currentAddressOf(customer.addresses)?.postCode)) missing.push("currentAddressPostcode");
  if (!customer.documents?.some((document) => privacyDocsNameList.includes(document?.name))) {
    missing.push("privacyConsent");
  }

  return missing;
};

/** `26` months reads as `2 years 2 months`. */
const formatMonths = (months: number): string => {
  const years = Math.floor(months / 12);
  const remainder = months % 12;
  const parts = [
    years > 0 ? `${years} ${years === 1 ? "year" : "years"}` : "",
    remainder > 0 || years === 0 ? `${remainder} ${remainder === 1 ? "month" : "months"}` : "",
  ];
  return parts.filter(Boolean).join(" ");
};

/** Whole cents as dollars, e.g. `500050` reads as `$5,000.50`. */
const money = (cents: number | undefined): string | undefined =>
  typeof cents === "number" ? formatCurrencyValue(cents / 100) : undefined;

/** A verified date as the app shows dates, e.g. `02-12-2015`. */
const readableDate = (value: string | undefined): string | undefined =>
  value ? formatToReadableDate(value) : undefined;

/** `2025/2026` for a payroll year across two calendar years, `2025` for one. */
const payrollYearLabel = (income: VerifiedEmployer["latestGrossIncome"]): string | undefined => {
  const { startYear, endYear } = income ?? {};
  if (startYear === undefined || endYear === undefined) return undefined;
  return startYear === endYear ? String(startYear) : `${startYear}/${endYear}`;
};

const toFields = (entries: [string, string | undefined][]): SummaryField[] =>
  entries.filter((entry): entry is [string, string] => isPresent(entry[1])).map(([label, value]) => ({ label, value }));

const employmentFields = (employment: VerifiedEmployment): SummaryField[] =>
  toFields([
    ["Employment status", employment.employmentStatus],
    ["Employment type", employment.employmentType],
    ["Job title", employment.jobTitle],
    ["Division", employment.division],
    ["Hire date", readableDate(employment.mostRecentHireDate ?? employment.originalHireDate)],
    ["Last day worked", readableDate(employment.lastDayWorked)],
    [
      "Time with employer",
      isPresent(employment.totalTimeWithEmployerMonths)
        ? formatMonths(employment.totalTimeWithEmployerMonths as number)
        : undefined,
    ],
  ]);

const incomeFields = ({ latestPayPeriod: pay, latestGrossIncome: year, dataCurrentAsOf }: VerifiedEmployer) => {
  const [startDate, endDate] = [readableDate(pay?.startDate), readableDate(pay?.endDate)];
  const yearLabel = payrollYearLabel(year);

  return toFields([
    ["Pay frequency", pay?.payFrequency],
    ["Latest pay period", startDate && endDate ? `${startDate} – ${endDate}` : (endDate ?? startDate)],
    ["Gross pay (latest period)", money(pay?.grossEarningsCents)],
    ["Net pay (latest period)", money(pay?.netEarningsCents)],
    ["Base pay (latest period)", money(pay?.basePayCents)],
    ["Hours worked (latest period)", isPresent(pay?.hoursWorked) ? String(pay?.hoursWorked) : undefined],
    [
      "Pay rate",
      isPresent(pay?.payRateCents)
        ? [money(pay?.payRateCents), pay?.payRateDescription].filter(Boolean).join(" ")
        : undefined,
    ],
    [yearLabel ? `Gross income ${yearLabel}` : "Gross income", money(year?.totalCents)],
    [yearLabel ? `Base income ${yearLabel}` : "Base income", money(year?.basePayCents)],
    ["Data current as of", readableDate(dataCurrentAsOf)],
  ]);
};

const FieldGrid = ({ fields }: { fields: SummaryField[] }) => (
  <Grid container spacing={2}>
    {fields.map(({ label, value }) => (
      <Grid key={label} xs={6} sm={4} md={3}>
        <Typography level="body-xs" textColor="text.tertiary">
          {label}
        </Typography>
        <Typography level="body-sm" bold>
          {value}
        </Typography>
      </Grid>
    ))}
  </Grid>
);

const VerifiedEmployerSummary = ({ employer }: { employer: VerifiedEmployer }) => (
  <Sheet variant="outlined" borderRadius="md" padding={3}>
    <Stack spacing={2}>
      <Stack>
        <Typography level="title-sm" bold>
          {employer.employerName ?? EMPLOYMENT_INCOME_VERIFICATION_COPY.unnamedEmployer}
        </Typography>
        {employer.employerAbn && (
          <Typography level="body-xs" textColor="text.tertiary">
            ABN {employer.employerAbn}
          </Typography>
        )}
      </Stack>
      {(employer.employments ?? []).map((employment, index) => (
        <FieldGrid key={`employment-${index}`} fields={employmentFields(employment)} />
      ))}
      <FieldGrid fields={incomeFields(employer)} />
    </Stack>
  </Sheet>
);

/**
 * Every check is ordered for 3 months for now, so the months selector is deliberately hidden. The server still accepts
 * 3, 12 or 36: to offer the choice again, uncomment the code marked "Months selector hidden" in this file.
 */
const EMPLOYMENT_INCOME_VERIFICATION_DEFAULT_MONTHS: EmploymentIncomeVerificationMonths =
  EMPLOYMENT_INCOME_VERIFICATION_MONTHS[0];

// Months selector hidden: laid out like the bank statements days, the selector this one mirrors.
// const MONTHS_OPTIONS = EMPLOYMENT_INCOME_VERIFICATION_MONTHS.map((months) => ({ label: String(months) }));
// const MONTHS_OPTION_STYLE = { width: "50%", padding: "8px", minHeight: "20px" };

/** A populated summary; an id string means the payload did not populate it, so there is nothing to show. */
const summaryOf = (value: EmploymentIncomeVerificationCustomer["employmentIncomeVerification"]) =>
  value && typeof value === "object" ? (value as EmploymentIncomeVerificationSummary) : undefined;

/** A reported field as the broker knows it: `employeeAddress.city` is the current address. Unlisted ones show as sent. */
const fieldLabel = (field: string): string => {
  const path = field.replace(/\[\d+\]/g, "");
  return (
    EMPLOYMENT_INCOME_VERIFICATION_FIELD_LABELS[path] ??
    EMPLOYMENT_INCOME_VERIFICATION_FIELD_LABELS[path.split(".")[0] ?? ""] ??
    path
  );
};

/** `Missing: date of birth.` for a list of field names; nothing when there is no list or it is empty. */
const describeFields = (prefix: string, fields: unknown): string | undefined => {
  const labels = Array.isArray(fields)
    ? fields.filter((field): field is string => typeof field === "string").map(fieldLabel)
    : [];
  return labels.length > 0 ? `${prefix} ${Array.from(new Set(labels)).join(", ")}.` : undefined;
};

/** Adds the fields a refusal names to its message, so the broker knows what to fix before trying again. */
const withRefusedFields = (message: string, res: IDispatchResponse): string => {
  const details = (res?.payload as { data?: Record<string, unknown> } | undefined)?.data;
  return [
    message,
    describeFields(EMPLOYMENT_INCOME_VERIFICATION_COPY.missingFields, details?.missingFields),
    describeFields(EMPLOYMENT_INCOME_VERIFICATION_COPY.rejectedFields, details?.invalidFields),
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * The customer's Equifax Verification Exchange (VOI) check: the verified employment and income, the PDF report, and
 * the paid check, offered until Equifax finds a match. A customer is verified once; a no-match can be tried again.
 */
const EmploymentIncomeVerification = ({
  customer,
  applicationId,
  isDisableEdit = false,
}: EmploymentIncomeVerificationProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { canUsePaidServices } = usePaidServices();
  const { accessComponents } = useAccessAppComponent();
  const permissions = useSelector<UserSlice, UserPermissionsDocument | undefined>(userPermissionsSelector);
  const { requestingEmploymentIncomeVerification } = useSelector<ApplicationFormSlice, UserDetailsState>(
    userDetailsSelector,
  );
  // Months selector hidden: restore this state in place of the fixed period below.
  // const [months, setMonths] = useState<EmploymentIncomeVerificationMonths>(EMPLOYMENT_INCOME_VERIFICATION_MONTHS[0]);
  const months = EMPLOYMENT_INCOME_VERIFICATION_DEFAULT_MONTHS;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { employmentIncomeVerificationTermsAcceptedAt } = useSelector<UserSlice, UserState>(userSelector);

  const isAvailable =
    Boolean(accessComponents.application.customer.isEmploymentIncomeVerificationEnabled) &&
    Boolean(permissions?.services?.employment_income_verification);

  if (!isAvailable) return null;

  const summary = summaryOf(customer.employmentIncomeVerification);
  const isMatched = summary?.status === EMPLOYMENT_INCOME_VERIFICATION_STATUS.MATCHED;
  const isNoMatch = summary?.status === EMPLOYMENT_INCOME_VERIFICATION_STATUS.NO_MATCH;
  const isLoading = Boolean(requestingEmploymentIncomeVerification?.[customer._id]);
  const missingRequirements = getMissingRequirements(customer);
  const reportDocuments = (customer.documents ?? []).filter(
    (document) => document?.name === DOCUMENT_TYPES.EMPLOYMENT_INCOME_VERIFICATION,
  );
  // Months selector hidden: per customer, as the page shows one of these sections for each customer on the application.
  // const monthsLabelId = `employment-income-verification-months-${customer._id}`;

  // Months selector hidden: the shared toggle group reports the chosen button's text; only an offered period is taken.
  // const handleMonthsChange = (value: string) => {
  //   const selected = EMPLOYMENT_INCOME_VERIFICATION_MONTHS.find((option) => String(option) === value);
  //   if (selected) setMonths(selected);
  // };

  const handleRequest = async () => {
    const res: IDispatchResponse = await dispatch(
      requestEmploymentIncomeVerification({ applicationId, customerId: customer._id, months }),
    );

    if (res?.error) {
      const toast = getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg);
      dispatch(openSnackbar({ ...toast, message: withRefusedFields(toast.message, res) }));
      return;
    }

    const result = res?.payload?.data?.data as
      | { employmentIncomeVerification?: Pick<EmploymentIncomeVerificationSummary, "status"> }
      | undefined;
    const isMatch = result?.employmentIncomeVerification?.status === EMPLOYMENT_INCOME_VERIFICATION_STATUS.MATCHED;

    dispatch(
      openSnackbar(
        isMatch
          ? getSuccessToastPayload(res, EMPLOYMENT_INCOME_VERIFICATION_COPY.successMessage)
          : {
              message: getToastSuccessMessage(res, EMPLOYMENT_INCOME_VERIFICATION_COPY.noMatchMessage),
              intent: "warning",
            },
      ),
    );
  };

  /** Once the broker has accepted the terms and consent, the card shows the check running in place of the modal. */
  const handleAgreed = () => {
    setIsModalOpen(false);
    handleRequest();
  };

  const cards: EntityServiceCardProps[] = [
    {
      id: "employmentIncomeVerification",
      logos: [SERVICE_PROVIDER_LOGOS.equifax],
      title: EMPLOYMENT_INCOME_VERIFICATION_COPY.title,
      subtitle: EMPLOYMENT_INCOME_VERIFICATION_COPY.subtitle,
      loadingSubtitle: EMPLOYMENT_INCOME_VERIFICATION_COPY.loadingSubtitle,
      isLoading,
      isDisabled: !canUsePaidServices || isDisableEdit || missingRequirements.length > 0,
      tooltip: canUsePaidServices ? null : <PaidServicesTooltip />,
      // Equifax's terms and the customer consent are asked for once, before the broker's first check.
      onClick: employmentIncomeVerificationTermsAcceptedAt ? handleRequest : () => setIsModalOpen(true),
    },
  ];

  // The parent is an MUI Grid container, which a Joy Grid cannot be an item of: the Box takes the full row itself.
  return (
    <Box sx={{ width: "100%", mb: "30px", pb: "30px", borderBottom: 1, borderColor: "divider" }}>
      <Typography level="title-sm" bold mb="15px">
        {EMPLOYMENT_INCOME_VERIFICATION_COPY.sectionTitle}
      </Typography>

      {isMatched && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {(summary?.verifiedEmployers ?? []).map((employer, index) => (
            <VerifiedEmployerSummary key={`${employer.employerName ?? "employer"}-${index}`} employer={employer} />
          ))}
          {summary?.reportPdfStatus === EMPLOYMENT_INCOME_VERIFICATION_REPORT_PDF_STATUS.FAILED && (
            <Typography level="body-xs" textColor="text.tertiary">
              {EMPLOYMENT_INCOME_VERIFICATION_COPY.reportPdfFailed}
            </Typography>
          )}
        </Stack>
      )}

      {isNoMatch && (
        <Stack sx={{ mb: 2 }}>
          <Typography level="body-sm" bold>
            {summary?.message || EMPLOYMENT_INCOME_VERIFICATION_COPY.noMatchMessage}
          </Typography>
          <Typography level="body-xs" textColor="text.tertiary">
            {EMPLOYMENT_INCOME_VERIFICATION_COPY.noMatchHint}
          </Typography>
        </Stack>
      )}

      {/* A match is final: the card and terms are offered until then, and the server refuses a repeat. */}
      {!isMatched && (
        <>
          {/* Months selector hidden for now, see EMPLOYMENT_INCOME_VERIFICATION_DEFAULT_MONTHS.
          <Grid container>
            <Grid xs={12} sm={6}>
              <Typography id={monthsLabelId} level="title-sm">
                {EMPLOYMENT_INCOME_VERIFICATION_COPY.monthsLabel}
              </Typography>
              <RadioButtonGroup
                aria-labelledby={monthsLabelId}
                disabled={isDisableEdit}
                name="employmentIncomeVerificationMonths"
                value={String(months)}
                handleValue={handleMonthsChange}
                optionStyle={MONTHS_OPTION_STYLE}
                options={MONTHS_OPTIONS}
              />
            </Grid>
          </Grid> */}

          <EntityServiceCardGroup cards={cards} />

          {missingRequirements.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography level="body-xs" textColor="text.tertiary">
                {`${EMPLOYMENT_INCOME_VERIFICATION_COPY.requires} ${missingRequirements
                  .map((requirement) => EMPLOYMENT_INCOME_VERIFICATION_REQUIREMENT_LABELS[requirement])
                  .join(", ")}`}
              </Typography>
            </Box>
          )}

          <EmploymentIncomeVerificationModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onAgreed={handleAgreed}
          />
        </>
      )}

      {reportDocuments.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {reportDocuments.map((document) => (
            <Document key={document._id} document={document} name="employmentIncomeVerification" />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default EmploymentIncomeVerification;
